import React, { useEffect, useRef } from 'react';
import { UnstyledButton } from '@mantine/core';
import classes from './pos-wheel.module.scss';

export type PosWheelItem = { key: string; label: string; selected: boolean; onClick: () => void; testid?: string };

export type PosWheelColumnProps = { items: PosWheelItem[]; disabled?: boolean };

// One scroll-snap wheel column with a centered selected pill; click a cell to select it.
export const PosWheelColumn: React.FC<PosWheelColumnProps> = ({ items, disabled }) => {
  const columnRef = useRef<HTMLDivElement>(null);
  const scrollTimerRef = useRef<number | null>(null);
  const selectedKey = items.find((item) => item.selected)?.key;

  // Center the selected cell under the pill on mount + when the selection changes (drum feel).
  useEffect(() => {
    const col = columnRef.current;
    const sel = col?.querySelector<HTMLElement>('[data-wheel-selected="true"]');
    if (col && sel) {
      col.scrollTop = sel.offsetTop - col.clientHeight / 2 + sel.clientHeight / 2;
    }
  }, [selectedKey]);

  useEffect(() => () => {
    if (scrollTimerRef.current !== null) {
      window.clearTimeout(scrollTimerRef.current);
    }
  }, []);

  const handleScroll = () => {
    if (scrollTimerRef.current !== null) {
      window.clearTimeout(scrollTimerRef.current);
    }
    scrollTimerRef.current = window.setTimeout(() => {
      scrollTimerRef.current = null;
      const column = columnRef.current;
      if (!column || disabled) return;

      const cells = Array.from(column.querySelectorAll<HTMLElement>('[data-wheel-key]'));
      const center = column.scrollTop + column.clientHeight / 2;
      const centeredIndex = cells.reduce((closestIndex, cell, index) => {
        const distance = Math.abs(cell.offsetTop + cell.offsetHeight / 2 - center);
        const closest = cells[closestIndex];
        const closestDistance = Math.abs(closest.offsetTop + closest.offsetHeight / 2 - center);
        return distance < closestDistance ? index : closestIndex;
      }, 0);
      const centeredItem = items[centeredIndex];
      if (centeredItem && !centeredItem.selected) {
        centeredItem.onClick();
      }
    }, 100);
  };

  return (
    <div ref={columnRef} className={classes.column} onScroll={handleScroll} data-testid="pos-wheel-column">
      <div className={classes.columnInner}>
        {items.map((item) => (
          <UnstyledButton
            key={item.key}
            component="button"
            type="button"
            disabled={disabled}
            data-wheel-key={item.key}
            data-wheel-selected={item.selected || undefined}
            data-testid={item.testid}
            className={item.selected ? `${classes.cell} ${classes.cellSelected}` : classes.cell}
            onClick={item.onClick}
          >
            {item.label}
          </UnstyledButton>
        ))}
      </div>
    </div>
  );
};

export type PosWheelProps = { children: React.ReactNode; dir?: 'ltr' | 'rtl' };

// Drum container: centered pill behind N PosWheelColumns; dir sets column order (ltr/rtl).
export const PosWheel: React.FC<PosWheelProps> = ({ children, dir = 'rtl' }) => (
  <div className={classes.wheels} dir={dir}>
    <div className={classes.selectionPill} aria-hidden="true" />
    {children}
  </div>
);
